import { AuthenticationService } from "@app/authentication";
import { ServiceBase } from "@app/shared";
import { SortDirections } from "../shared/waffles";
import { UserInfo } from "./UserContext";
import { GuestLoginInfo } from "./guestLoginInfo";
import { DashboardType } from "./enumerations";
import { NewUserModel, UserModelFromApi, UserModelToApi, UserStatus, UserClaim } from "@app/admin/user-administration/userModel";
import { querystringify } from "@app/shared/utility";

/** Manages the retrieval and manipulation of user information. */
export default class UserService extends ServiceBase {
  private authenticationApiEndpoint = `${this.authenticationEndpoint}/api`;

  /** Retrieves the user information for the currently authenticated user. Returns null if there is no currently authenticated user */
  public async get(): Promise<UserInfo> {
    const oidcUser = await AuthenticationService.getInstance().getUser();

    if (oidcUser) {
      const user = await this.makeGet<UserInfo>("users/info");

      const guestLoginInfo = await this.getGuestLogins();

      return UserImpl.createFromUser(user, guestLoginInfo);
    } else {
      return null;
    }
  }

  /** Updates the currently-authenticated user - can't be used for admin user updates */
  public async updateUserProfile(user: UserInfo): Promise<void> {
    await this.makePatch<void>("/account/update-user-profile", user, false, this.authenticationApiEndpoint);
  }

  public async getAllUsers(skip = 0, take = 100, formalNameSearch?: string, userNameSearch?: string, emailSearch?: string, clientNameSearch?: string, userStatusSearch?: UserStatus[], sortDirection?: SortDirections, sortedColumn?: string): Promise<UserModelFromApi[]> {
    const sortDirectionEnumValue = sortDirection === SortDirections.Asc ? 3 : sortDirection === SortDirections.Desc ? 2 : 1;
    const query = {
      clientNameSearch,
      emailSearch,
      formalNameSearch,
      offset: skip,
      pageSize: take,
      sortDirection: sortDirectionEnumValue,
      sortedColumn,
      userNameSearch,
      userStatusSearch: this.getUserStatusSearch(userStatusSearch),
    };

    return await this.makeGet(`/users?${querystringify(query)}`)
  }

  public async getUserCount(formalNameSearch?: string, userNameSearch?: string, emailSearch?: string, clientNameSearch?: string, userStatusSearch?: UserStatus[]): Promise<number> {
    const query = {
      clientNameSearch,
      emailSearch,
      formalNameSearch,
      userNameSearch,
      userStatusSearch: this.getUserStatusSearch(userStatusSearch),
    };

    return await this.makeGet(`/users/count?${querystringify(query)}`);
  }

  private getUserStatusSearch = (userStatusSearchArray?: UserStatus[]): number => {

    let userStatusSearch: number = UserStatus.ACTIVATED | UserStatus.DEACTIVATED | UserStatus.UNCONFIRMED;
    if (userStatusSearchArray && userStatusSearchArray.length > 0) {
      userStatusSearch = 0;
      for (let i = 0; i < userStatusSearchArray.length; i++) {
        userStatusSearch |= userStatusSearchArray[i];
      }
    }

    return userStatusSearch;
  }

  public async getUsersByClient(clientId: number, skip = 0, take = 100, formalNameSearch?: string, userNameSearch?: string, emailSearch?: string, clientNameSearch?: string, userStatusSearch?: UserStatus[], sortDirection?: SortDirections, sortedColumn?: string): Promise<UserModelFromApi[]> {
    const query = {
      clientNameSearch,
      emailSearch,
      formalNameSearch,
      offset: skip,
      pageSize: take,
      sortDirection: sortDirection || 0,
      sortedColumn,
      userNameSearch,
      userStatusSearch: this.getUserStatusSearch(userStatusSearch),
    };

    return await this.makeGet(`/users/client/${clientId}?${querystringify(query)}`)
  }

  public async getUserCountByClient(clientId: number, formalNameSearch?: string, userNameSearch?: string, emailSearch?: string, clientNameSearch?: string, userStatusSearch?: UserStatus[]): Promise<number> {
    const query = {
      clientNameSearch,
      emailSearch,
      formalNameSearch,
      userNameSearch,
      userStatusSearch: this.getUserStatusSearch(userStatusSearch),
    };

    return await this.makeGet(`/users/client/${clientId}/count?${querystringify(query)}`);
  }

  /** Gets the guest logins for the currently-authenticated user */
  public async getGuestLogins(): Promise<GuestLoginInfo> {
    return await this.makeGet<GuestLoginInfo>("/account/guest-logins", null, this.authenticationApiEndpoint);
  }

  public async validateAndUpdateSession(): Promise<boolean> {
    return await this.makeGet<boolean>("/account/validate-and-update-session", null, this.authenticationApiEndpoint);
  }

  /**
   * Creates a new user and returns the user id of the new user
   * @param userModel
   */
  public async createUser(userModel: NewUserModel): Promise<number> {
    return await this.makePost<number>("/account/create-user", userModel, this.authenticationApiEndpoint);
  }

  public async createUsers(userModels: NewUserModel[], applyDefaultRoles: boolean): Promise<number> {
    return await this.makePost<number>(`/account/bulk-create-users?applyDefaultRoles=${applyDefaultRoles}`, userModels, this.authenticationApiEndpoint);
  }

  public async updateUser(userModel: UserModelToApi): Promise<void> {
    await this.makePost("/account/update-user", userModel, this.authenticationApiEndpoint);
  }

  public async getUser(userId: number): Promise<UserModelFromApi> {
    return await this.makeGet(`/users/${userId}`);
  }

  public async isUsernameAvailable(userName: string): Promise<boolean> {
    return await this.makeGet(`/account/is-username-available?userName=${userName}`, null, this.authenticationApiEndpoint);
  }

  public async isEmailAvailable(email: string, clientId: number): Promise<boolean> {
    return await this.makeGet(`/account/is-email-available?email=${email}&clientId=${clientId}`, null, this.authenticationApiEndpoint);
  }

  public async resendWelcomeEmail(userId: number): Promise<void> {
    await this.makePost(`/account/resend-welcome-email/${userId}`, null, this.authenticationApiEndpoint);
  }

  public async activateUser(userId: number, isActive: boolean): Promise<void> {
    await this.makePatch(`/account/activate-user/${userId}/${isActive}`, null, null, this.authenticationApiEndpoint);
  }

  public async deleteUser(userId: number, isDeleted: boolean): Promise<void> {
    await this.makePatch(`/account/delete-user/${userId}/${isDeleted}`, null, null, this.authenticationApiEndpoint);
  }

  public async createGuestLogins(userId: number): Promise<void> {
    await this.makePatch(`/account/create-quest-guest-logins-by-user/${userId}`, null, null, this.authenticationApiEndpoint);
  }

  public async getUserClaims(): Promise<UserClaim[]> {
    return await this.makeGet(`/account/get-user-claims`, null, this.authenticationApiEndpoint);
  }

  public async addUserClaim(claim: UserClaim): Promise<void> {
    await this.makePost(`/account/add-user-claim`, claim, this.authenticationApiEndpoint);
  }

  public async removeUserClaim(claim: UserClaim): Promise<void> {
    await this.makePost(`/account/remove-user-claim`, claim, this.authenticationApiEndpoint);
  }

  public async getLastLoginDate(): Promise<boolean> {
    return await this.makeGet<boolean>("/account/last-login-date", null, this.authenticationApiEndpoint);
  }
}

/** A user implementation to aid in user authorization handling */
class UserImpl implements UserInfo {
  public static createFromUser(
    user: UserInfo,
    guestLoginInfo: GuestLoginInfo): UserInfo {
    return !user
      ? user
      : new UserImpl(
        user.authorizedDashboardTypes,
        user.canDownload,
        user.expiration,
        user.name,
        user.roles,
        user.userId,
        user.displayName,
        user.maintenanceTime,
        user.showMaintenanceAlert,
        user.showMaintenancePage,
        user.email,
        user.firstName,
        user.middleInitial,
        user.lastName,
        user.address,
        user.city,
        user.state,
        user.zip,
        user.phoneNumber,
        guestLoginInfo,
        !guestLoginInfo.hasGuestAccounts || (guestLoginInfo.currentAccount.guestUserId === guestLoginInfo.parentAccount.guestUserId),
        user.clientId,
        user.clientName);
  }

  //private readonly authorizationService: AuthorizationService;

  constructor(
    public readonly authorizedDashboardTypes: DashboardType[],
    public readonly canDownload: boolean,
    public readonly expiration: number,
    public readonly name: string,
    public readonly roles: string[],
    public readonly userId: number,
    public readonly displayName: string,
    public readonly maintenanceTime: string,
    public readonly showMaintenanceAlert: boolean,
    public readonly showMaintenancePage: boolean,
    public readonly email: string,
    public readonly firstName: string,
    public readonly middleInitial: string,
    public readonly lastName: string,
    public readonly address: string,
    public readonly city: string,
    public readonly state: string,
    public readonly zip: string,
    public readonly phoneNumber: string,
    public readonly guestLoginInfo: GuestLoginInfo,
    public readonly isPrimaryAccount: boolean,
    public readonly clientId: number,
    public readonly clientName: string) {
    //this.authorizationService = new AuthorizationService(authenticationToken);
  }
}
